'use client';

import Cookies from 'cookies-js';
import { useParams, usePathname, useRouter } from 'next/navigation';
import type React from 'react';
import { useEffect, useMemo } from 'react';
import { setDefaultLocale } from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import { MainNavigationProvider } from '@/legacy/components/app/NavigationV2/NavigationProvider';
import { QuickMenuProvider } from '@/legacy/components/app/QuickMenu/QuickMenuProvider';
import { EntityExplorerCoordinator } from '@/legacy/components/EntityExplorer/EntityExplorerCoordinator';
import useBranch from '@/legacy/data/branch/useBranch';
import useBranches from '@/legacy/data/branch/useBranches';
import useMarketCode from '@/legacy/data/branch/useMarketCode';
import { EntityExplorerProvider } from '@/legacy/state/EntityExplorer/EntityExplorerProvider';
import SnackbarProvider from '@/legacy/state/Snackbar/SnackbarProvider';
import { FocusSkip } from '@/legacy/styled-components/components/FocusSkip';
import { PortalElement, PortalNamespace } from '@/legacy/styled-components/components/Portal';
import Snackbar from '@/legacy/styled-components/components/Snackbar';
import Fonts from '@/legacy/styled-components/layout/fonts';
import { getDatePickerLocale } from '@/legacy/utils/locale-cookie';
import { getLanguageWithLocaleOverride } from '@/legacy/utils/market';

import AppBodyContent from './AppBodyContent';

const selectedBranchCookieName = 'selectedBranch';

interface AppShellProps {
  children: React.ReactNode;
}

/**
 * Inner shell component that uses auth-dependent hooks.
 * Must be rendered inside AuthProvider.
 */
const AppShellInner = ({ children }: AppShellProps) => {
  const { t } = useTranslation(['mainNavigation', 'other', 'validation']);
  const params = useParams();
  const router = useRouter();
  const pathname = usePathname();
  const branchId = params?.branchId as string | undefined;

  const [branches] = useBranches();
  const [marketCode] = useMarketCode();
  const [branch] = useBranch();

  useEffect(() => {
    if (branchId) {
      Cookies.set(selectedBranchCookieName, branchId, {
        expires: Number.POSITIVE_INFINITY,
      });
    }
  }, [branchId]);

  const _branchId = useMemo(() => {
    const fromCookie = Cookies.get(selectedBranchCookieName);
    const preffered = fromCookie
      ? branches?.find((x) => x.id === fromCookie) || branches?.[0]
      : branches?.[0];
    return branchId || preffered?.id;
  }, [branchId, branches]);

  useEffect(() => {
    if (_branchId && pathname === '/') {
      router.replace(`/${_branchId}`);
    }
  }, [_branchId, pathname, router]);

  useEffect(() => {
    if (branch?.marketCode) {
      const locale = getLanguageWithLocaleOverride(branch.marketCode);
      const datePickerLocale = getDatePickerLocale(locale);
      setDefaultLocale(datePickerLocale);
    }
  }, [branch?.marketCode]);

  return (
    <SnackbarProvider>
      <QuickMenuProvider>
        <EntityExplorerProvider>
          <MainNavigationProvider>
            <Fonts market={marketCode} />
            <FocusSkip as="a" href="#main" title={t('goToMainContent', { ns: 'other' }) || ''}>
              {t('goToMainContent', { ns: 'other' })}
            </FocusSkip>

            <Snackbar />
            <AppBodyContent>{children}</AppBodyContent>
            <EntityExplorerCoordinator />
            <PortalElement id={PortalNamespace.DefaultPortal} />
            <PortalElement id={PortalNamespace.SubPortal} />
            <PortalElement id={PortalNamespace.Lightbox} />
          </MainNavigationProvider>
        </EntityExplorerProvider>
      </QuickMenuProvider>
    </SnackbarProvider>
  );
};

const AppShell = ({ children }: AppShellProps) => {
  // AuthProvider is now at the root level in ClientProviders
  return <AppShellInner>{children}</AppShellInner>;
};

export default AppShell;
